// ── Shared date-rendering authority ─────────────────────────────────
// Every date the portal renders is one of exactly two kinds, and routing a
// field through the wrong formatter IS the day-off bug this module exists
// to prevent (team-reported on Mark Reviewed; first fixed in fc29838,
// regressed because the fix's bare-date guard never saw Dataverse's wire
// shape):
//
//  INSTANT — a moment in time: scan executed_at, note createdon/modifiedon,
//  logins, ticket filings, override updated_at. Stored as a full UTC
//  timestamp; render in the viewer's local timezone. new Date(iso) is
//  CORRECT for these — that's what "local rendering of a moment" means.
//
//  DATE-ONLY — a calendar date with no meaningful time: review dates,
//  revisit-by deadlines, next/last audit dates. Dataverse DateOnly /
//  TimeZoneIndependent columns return these over the wire as
//  "YYYY-MM-DDT00:00:00Z" — a full ISO string that new Date() parses as UTC
//  midnight and renders A DAY EARLY for any viewer west of UTC. Render the
//  Y-M-D verbatim; never do timezone math on a calendar date.
//
// Column formats verified via EntityDefinitions metadata (patriotdelivery,
// 2026-07-08): pc_reviewedon, pc_revisitby, pc_nextauditdate,
// crc37_lastauditdate are DateOnly/TimeZoneIndependent; pc_lastlogin is
// DateAndTime/UserLocal (an instant). first_identified/last_reviewed come
// from scoring's executed_at.isoformat() (instants).

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

// Calendar-date value ("2026-07-08" or "2026-07-08T00:00:00Z") → Date at
// LOCAL midnight, or null. Exported so day-math (staleness, days-until
// pills) classifies days the same way rendering does.
export const parseDateOnly = (value) => {
  if (!value) return null;
  const m = DATE_ONLY_RE.exec(String(value));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

// Render a DATE-ONLY value verbatim. No timezone math, ever.
export const fmtDateOnly = (
  value,
  opts = { month: "short", day: "numeric", year: "numeric" },
  fallback = "—",
) => {
  const d = parseDateOnly(value);
  return d ? d.toLocaleDateString("en-US", opts) : fallback;
};

// Render an INSTANT in the viewer's local timezone. Pass hour/minute in
// opts to include the time of day.
export const fmtInstant = (
  iso,
  opts = { month: "short", day: "numeric", year: "numeric" },
  fallback = "—",
) => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return fallback;
  return (opts.hour || opts.minute)
    ? d.toLocaleString("en-US", opts)
    : d.toLocaleDateString("en-US", opts);
};

// True when a DATE-ONLY deadline is strictly before today (local calendar) —
// due-today is NOT past.
export const isDateOnlyPast = (value) => {
  const d = parseDateOnly(value);
  if (!d) return false;
  const now = new Date();
  return d < new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// True when a DATE-ONLY value is strictly AFTER today (local calendar).
// The twin of isDateOnlyPast, and deliberately NOT its negation: both
// return false for today and for null, so `today` belongs to neither.
//
// TODAY IS EXCLUDED ON PURPOSE (operator decision 2026-08-08). Callers
// use this to decide whether a forward-looking date is worth showing a
// customer. Since Phase A.5, recording "Booking Sent" stamps
// pc_nextauditdate to TODAY — a booking link went out, nothing is
// scheduled — so a guard that included today would tell that customer
// they have a session today, which is the bug. A genuine meeting booked
// for today stops showing on the morning of, which costs nothing: they
// are in the meeting.
export const isDateOnlyFuture = (value) => {
  const d = parseDateOnly(value);
  if (!d) return false;
  const now = new Date();
  return d > new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// Today's local calendar date as "YYYY-MM-DD" — the write-side twin of
// fmtDateOnly. Date-only stamps (Mark Reviewed) send this; never
// toISOString()/UTC, which flips to tomorrow's date during a local evening.
export const localDateString = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
